import type { MiloStatus, MiloTier } from "@keydials/api-client";

/**
 * The Milo card's words (KEY-716), as plain data so they can be tested: how a rung of the ladder reads, and what the
 * household holds against it.
 */

/** "£7.99 a month" / "£79.90 a year". */
export function tierPrice(tier: Pick<MiloTier, "pence">, annual: boolean): string {
  return `£${(tier.pence / 100).toFixed(2)} a ${annual ? "year" : "month"}`;
}

/** "600 requests a month · £7.99 a month". The requests are always per month, whatever the billing period. */
export function tierLabel(tier: MiloTier, annual: boolean): string {
  return `${tier.requests} requests a month · ${tierPrice(tier, annual)}`;
}

/**
 * What the card says above the picker: where the household stands this month, or that it has no Milo yet. Null when
 * billing is off, where there is no pool to describe and no tier to pick.
 */
export function miloPositionLine(status: MiloStatus | null): string | null {
  if (!status || status.allowance == null || status.used == null) return null;
  if (!status.tierRequests) return "No Milo on this account yet.";
  const left = Math.max(0, status.allowance - status.used);
  return `${left} of ${status.allowance} requests left this month.`;
}

/**
 * Whether a change of rung is a step up — bought and charged now — or a step down, which takes effect from the next
 * period; the same rule as every other add-on. Null when nothing changes.
 */
export function tierChangeNote(current: number | null | undefined, next: number): string | null {
  const now = current ?? 0;
  if (next === now) return null;
  if (next === 0) return "Milo comes off your plan at the end of this billing period. Nothing is refunded.";
  if (next > now) return "You'll have the larger allowance straight away, and the difference is charged now, pro rata.";
  return "The smaller allowance applies from your next billing period. Nothing is refunded for this one.";
}

/** "Plus 180 top-up requests, which carry over." — or null with none (KEY-717). */
export function topUpLine(status: Pick<MiloStatus, "topUpRemaining"> | null): string | null {
  if (!status?.topUpRemaining || status.topUpRemaining <= 0) return null;
  return `Plus ${status.topUpRemaining} top-up request${status.topUpRemaining === 1 ? "" : "s"}, which carry over.`;
}
